import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AccessDataService, type PlanUpdatePayload, type UserAccess } from '../services/AccessDataService';

export const accessQueryKeys = {
  current: ['access', 'current'] as const,
  plans: ['access', 'plans'] as const,
  adminPlans: ['access', 'adminPlans'] as const,
};

export function useCurrentAccessQuery(enabled = true) {
  return useQuery({
    queryKey: accessQueryKeys.current,
    queryFn: AccessDataService.getCurrentAccess,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useRedeemPromoCodeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => AccessDataService.redeemPromoCode(code),
    onSuccess: (access: UserAccess) => {
      queryClient.setQueryData(accessQueryKeys.current, access);
      queryClient.invalidateQueries({ queryKey: accessQueryKeys.current });
    },
  });
}

export function usePlansQuery(enabled = true) {
  return useQuery({
    queryKey: accessQueryKeys.plans,
    queryFn: AccessDataService.getPlans,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useAdminPlansQuery(enabled = true) {
  return useQuery({
    queryKey: accessQueryKeys.adminPlans,
    queryFn: AccessDataService.getAdminPlans,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useSelectPlanMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planCode: string) => AccessDataService.selectPlan(planCode),
    onSuccess: (access: UserAccess) => {
      queryClient.setQueryData(accessQueryKeys.current, access);
      queryClient.invalidateQueries({ queryKey: accessQueryKeys.current });
      queryClient.invalidateQueries({ queryKey: accessQueryKeys.plans });
    },
  });
}

export function useUpdatePlanMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planCode, payload }: { planCode: string; payload: PlanUpdatePayload }) =>
      AccessDataService.updatePlan(planCode, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessQueryKeys.adminPlans });
      queryClient.invalidateQueries({ queryKey: accessQueryKeys.plans });
      queryClient.invalidateQueries({ queryKey: accessQueryKeys.current });
    },
  });
}
